/**
 * useMemberProfile — consumer hook for `MemberProfilePort`.
 *
 * Reads the `memberProfile` port from PortsContext, calls `getProfile()`, and
 * resolves the async result via `React.use()`. Throws if the provider or the
 * port slot is absent.
 *
 * Promise-caching discipline is identical to `useCases` (034 US1) / `useMyData`
 * (033) and for the same reason: the production port is genuinely ASYNC (the
 * tRPC `TrpcMemberProfileReader`), and a component that suspends on its INITIAL
 * mount has its fiber discarded — a `useRef`-cached promise would be recreated
 * every retry and the boundary would loop forever. The cache therefore lives
 * OUTSIDE React in a module-level WeakMap keyed by the port instance: it
 * survives the suspend-retry, resets when the composition swaps the adapter (new
 * key), and is GC'd with the port. One read per port instance per app lifetime;
 * the profile re-fetches on reload (route-on-read — no local copy, FR-009).
 *
 * Only call inside a component wrapped in a Suspense boundary; the profile route
 * wraps its body in `PortErrorBoundary(memberProfile)` + Suspense.
 *
 * Owned by feature 018-omat-tiedot; reshaped by 034-member-is-reads US2.
 */

import React, { useContext, useSyncExternalStore } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type {
  ContactDetailsPatch,
  MemberProfile,
  MemberProfilePort,
  MemberWriteOutcome,
} from './MemberProfilePort.js';

interface ProfileCacheEntry {
  readonly promise: PromiseLike<MemberProfile>;
  rejected: boolean;
}

const profilePromiseCache = new WeakMap<MemberProfilePort, ProfileCacheEntry>();

// ── Invalidation store (035) ────────────────────────────────────────────────
// A successful write must make the surface re-read LIVE (route-on-read, no local
// mirror — FR-009). `invalidateMemberProfile` evicts the cached read promise and
// bumps a module-level version; `useMemberProfile` subscribes via
// `useSyncExternalStore`, so every reader re-renders, finds an empty cache, and
// re-fetches (re-suspending briefly). No written value is copied into local state.
let profileVersion = 0;
const profileListeners = new Set<() => void>();
function subscribeProfile(cb: () => void): () => void {
  profileListeners.add(cb);
  return () => profileListeners.delete(cb);
}
function getProfileVersion(): number {
  return profileVersion;
}

/** Evict the cached read for `port` and notify readers to re-fetch (post-write). */
export function invalidateMemberProfile(port: MemberProfilePort): void {
  profilePromiseCache.delete(port);
  profileVersion += 1;
  for (const l of profileListeners) {
    l();
  }
}

function isPromiseLike(
  value: MemberProfile | PromiseLike<MemberProfile>,
): value is PromiseLike<MemberProfile> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

export function useMemberProfile(): MemberProfile {
  // Re-render when a write invalidates the profile so the read re-runs (FR-009).
  useSyncExternalStore(subscribeProfile, getProfileVersion, getProfileVersion);
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useMemberProfile: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.memberProfile === undefined) {
    throw new Error(
      'useMemberProfile: memberProfile port is not registered. This feature requires the member profile port to be configured.',
    );
  }

  const port = ports.memberProfile;

  let entry = profilePromiseCache.get(port);
  if (entry === undefined) {
    const profileOrPromise = port.getProfile();
    if (!isPromiseLike(profileOrPromise)) {
      // Synchronous adapter (test fixture) — nothing to suspend on.
      return profileOrPromise;
    }
    const newEntry: ProfileCacheEntry = { promise: profileOrPromise, rejected: false };
    profileOrPromise.then(undefined, () => {
      newEntry.rejected = true;
    });
    profilePromiseCache.set(port, newEntry);
    entry = newEntry;
  } else if (entry.rejected) {
    // Deferred eviction (the useCases / useMyData rationale): a permanently-
    // cached rejected promise would brick the surface on every later mount, but
    // eager eviction makes React's synchronous error-retry re-fetch in an
    // unbounded loop. queueMicrotask lands the eviction AFTER React commits the
    // boundary fallback — retries still observe the rejected entry; a later
    // mount (navigate-away-and-back) finds an empty cache and re-fetches.
    queueMicrotask(() => profilePromiseCache.delete(port));
  }

  return React.use(entry.promise);
}

/**
 * useMemberProfileWriter — the bound contact writer (035 US1).
 *
 * Returns `updateContact` bound to the registered
 * `memberProfile` port. On a successful write it invalidates the read cache so
 * the surface re-reads live (route-on-read, no local mirror — FR-009). Any thrown
 * transport/config error is caught and surfaced as an honest `unavailable`
 * outcome (the member sees "couldn't save, try again"), never a raw error and
 * never a false success (SC-006).
 */
export function useMemberProfileWriter(): {
  updateContact: (
    patch: ContactDetailsPatch,
    idempotencyKey: string,
  ) => Promise<MemberWriteOutcome>;
} {
  const ports = useContext(PortsContext);
  if (ports === null) {
    throw new Error(
      'useMemberProfileWriter: PortsContext not found. Wrap the component in a PortsProvider.',
    );
  }
  if (ports.memberProfile === undefined) {
    throw new Error('useMemberProfileWriter: memberProfile port is not registered.');
  }
  const port = ports.memberProfile;
  return {
    async updateContact(patch, idempotencyKey) {
      try {
        const outcome = await port.updateContact(patch, idempotencyKey);
        if (outcome.status === 'ok') {
          invalidateMemberProfile(port);
        }
        return outcome;
      } catch (err) {
        // Honest `unavailable` to the member, but leave a diagnostic — otherwise a
        // misconfigured/unwired port presents as a permanent transient outage with
        // no trace. No PII: the error, not the patch.
        // biome-ignore lint/suspicious/noConsole: deliberate observability — a silent write-throw would make a misconfigured port undiagnosable
        console.error('useMemberProfileWriter.updateContact threw', err);
        return { status: 'unavailable' };
      }
    },
  };
}
